import type { JSX } from 'react';

import clsx from 'clsx';

import { inputAffixBackgroundClasses } from '@/components/classes/inputAffixBackgroundClasses';
import { inputBorderClasses } from '@/components/classes/inputBorderClasses';
import { testIds } from '@/components/constants/testIds';
import { composeEndRadiusClasses } from '@/components/functions/composeEndRadiusClasses';
import { composeStartRadiusClasses } from '@/components/functions/composeStartRadiusClasses';
import type { Color } from '@/components/types/Color';
import type { IconType } from '@/components/types/IconType';
import type { InputBackground } from '@/components/types/InputBackground';
import type { InputBorder } from '@/components/types/InputBorder';
import type { Radius } from '@/components/types/Radius';
import type { TextSize } from '@/components/types/TextSize';
import { componentsConfig } from '@/config/componentsConfig';

import type { Adaptive } from '../../types/Adaptive';
import HStack from '../HStack';
import Icon from '../Icon';
import Text from '../Text';

type InputAffixProps = {
    direction?: 'start' | 'end';
    text?: string;
    icon?: IconType;
    textSize?: Adaptive<TextSize>;
    radius?: Adaptive<Radius>;
    background?: InputBackground;
    color?: Color;
    border?: InputBorder;
    className?: string;
    affixClassName?: string;
    testId?: string;
};

export default function InputAffix({
    direction = componentsConfig.inputAffix.direction,
    text,
    icon,
    textSize,
    radius = componentsConfig.inputAffix.radius,
    background = componentsConfig.inputAffix.background,
    color = componentsConfig.inputAffix.color,
    border = componentsConfig.inputAffix.border,
    className,
    affixClassName,
    testId = testIds.inputAffix.base,
}: InputAffixProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <HStack
            className={clsx(
                direction === 'start' ? composeStartRadiusClasses(radius) : composeEndRadiusClasses(radius),
                inputAffixBackgroundClasses[background],
                border !== 'transparent' && inputBorderClasses[border],
                background === 'transparent' ? 'ps-4' : 'px-4',
                className
            )}
            width="auto"
            alignment="center"
            testId={testId}
        >
            {icon && <Icon icon={icon} className={clsx(affixClassName)} testId={testIds.inputAffix.icon} color={color} />}
            {text && (
                <Text size={textSize} color={color} className={affixClassName} testId={testIds.inputAffix.text}>
                    {text}
                </Text>
            )}
        </HStack>
    );
}
